// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getSharedBffWebSocket, GolfCourseChangedEvent, GolfCourseUpsertRejectedEvent } from '@/services/bffWebSocket';
import { useUser } from './user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';

const HOLE_COUNT = 18;

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};

type HoleRow = { number: number; par: string; strokeIndex: string };
type TeeRow = {
  id: string;
  name: string;
  sex: string;
  courseRating: string;
  slopeRating: string;
};

type SaveState = {
  status: 'idle' | 'saving' | 'success' | 'error';
  message?: string | null;
};

type RoundSummary = {
  courseId?: string;
  CourseId?: string;
  courseName?: string;
  CourseName?: string;
  courseAddress?: string;
  CourseAddress?: string;
};

type GeoCourseDetail = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
  address?: string;
  Address?: string;
  holes?: Array<{ number?: number; Number?: number; par?: number; Par?: number; strokeIndex?: number; StrokeIndex?: number }>;
  Holes?: Array<{ number?: number; Number?: number; par?: number; Par?: number; strokeIndex?: number; StrokeIndex?: number }>;
  tees?: Array<{
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    sex?: string;
    Sex?: string;
    courseRating?: number;
    CourseRating?: number;
    slopeRating?: number;
    SlopeRating?: number;
    par?: number;
    Par?: number;
  }>;
  Tees?: Array<{
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    sex?: string;
    Sex?: string;
    courseRating?: number;
    CourseRating?: number;
    slopeRating?: number;
    SlopeRating?: number;
    par?: number;
    Par?: number;
  }>;
};

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeGuid = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!GUID_REGEX.test(trimmed)) return null;
  return trimmed.toLowerCase();
};

export default function ConfigCourseScreen() {
  const { roundId: roundIdParam, closeOnSave: closeOnSaveParam } = useLocalSearchParams<{ roundId?: string; closeOnSave?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const { user } = useUser();
  const storedRoundId = useRoundState((state) => state.roundId);
  const routeRoundId = useMemo(() => (Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam) ?? null, [roundIdParam]);
  const closeOnSave = useMemo(() => {
    const value = (Array.isArray(closeOnSaveParam) ? closeOnSaveParam[0] : closeOnSaveParam) ?? '';
    return value === '1' || value === 'true';
  }, [closeOnSaveParam]);
  const effectiveRoundId = routeRoundId ?? storedRoundId;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [hydratingCourse, setHydratingCourse] = useState(false);
  const [clubName, setClubName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [holes, setHoles] = useState<HoleRow[]>(
    Array.from({ length: HOLE_COUNT }, (_, idx) => ({
      number: idx + 1,
      par: '4',
      strokeIndex: String(idx + 1),
    }))
  );
  const [tees, setTees] = useState<TeeRow[]>([
    {
      id: `tee-${Date.now()}`,
      name: 'Yellow',
      sex: 'male',
      courseRating: '72.0',
      slopeRating: '113',
    },
  ]);
  const [teePar, setTeePar] = useState('72');
  const [genderPickerTeeId, setGenderPickerTeeId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle', message: null });

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      const courseChanged = message as GolfCourseChangedEvent;
      if (courseChanged.type === 'golfCourse.changed') {
        const requestId = courseChanged.requestId ?? courseChanged.RequestId;
        if (!requestId || pendingRequestIdRef.current !== requestId) {
          return;
        }
        const changedCourseId = normalizeGuid(courseChanged.courseId ?? courseChanged.CourseId ?? null);
        if (changedCourseId) {
          setEditingCourseId(changedCourseId);
        }
        pendingRequestIdRef.current = null;
        setSaveState({
          status: 'success',
          message: t('courseConfig.saveSuccess', { id: changedCourseId ?? courseChanged.courseId ?? courseChanged.CourseId ?? '' }),
        });
        if (closeOnSave) {
          setTimeout(() => {
            router.back();
          }, 150);
        }
        return;
      }
      const rejected = message as GolfCourseUpsertRejectedEvent;
      if (rejected.type === 'golfCourse.upsert.rejected') {
        if (!rejected.requestId || pendingRequestIdRef.current !== rejected.requestId) {
          return;
        }
        pendingRequestIdRef.current = null;
        setSaveState({ status: 'error', message: t('courseConfig.saveRejected') });
      }
    });
    return () => unsubscribe();
  }, [closeOnSave, isFocused, router, t, user?.token]);

  useEffect(() => {
    if (!effectiveRoundId || !user?.token || !isNavigationReady) {
      return;
    }

    let cancelled = false;
    const loadRoundCourse = async () => {
      setHydratingCourse(true);
      try {
        const roundResponse = await fetch(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!roundResponse.ok) {
          return;
        }

        const round = (await roundResponse.json()) as RoundSummary;
        const roundCourseName = (round.courseName ?? round.CourseName ?? '').trim();
        const roundCourseAddress = (round.courseAddress ?? round.CourseAddress ?? '').trim();
        const roundCourseId = normalizeGuid(round.courseId ?? round.CourseId ?? null);
        if (cancelled) return;

        if (roundCourseName) {
          setCourseName(roundCourseName);
        }
        if (roundCourseAddress) {
          setClubName(roundCourseAddress);
        }
        if (!roundCourseId) {
          return;
        }

        setEditingCourseId(roundCourseId);
        const courseResponse = await fetch(`${apiBaseUrl}/api/geodata/courses/${roundCourseId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!courseResponse.ok) {
          return;
        }

        const course = (await courseResponse.json()) as GeoCourseDetail;
        if (cancelled) return;

        const nextCourseName = (course.name ?? course.Name ?? '').trim();
        const nextAddress = (course.address ?? course.Address ?? '').trim();
        if (nextCourseName) {
          setCourseName(nextCourseName);
        }
        if (nextAddress) {
          setClubName(nextAddress);
        }

        const incomingHoles = (course.holes ?? course.Holes ?? [])
          .map((hole) => {
            const number = hole.number ?? hole.Number ?? 0;
            const par = hole.par ?? hole.Par ?? 4;
            const strokeIndex = hole.strokeIndex ?? hole.StrokeIndex ?? number;
            return {
              number,
              par: String(par),
              strokeIndex: String(strokeIndex),
            };
          })
          .filter((hole) => hole.number > 0)
          .sort((a, b) => a.number - b.number);
        if (incomingHoles.length > 0) {
          setHoles(incomingHoles);
        }

        const incomingTees = (course.tees ?? course.Tees ?? [])
          .map((tee) => {
            const name = (tee.name ?? tee.Name ?? '').trim();
            const sex = (tee.sex ?? tee.Sex ?? '').trim().toLowerCase();
            const courseRating = tee.courseRating ?? tee.CourseRating;
            const slopeRating = tee.slopeRating ?? tee.SlopeRating;
            const par = tee.par ?? tee.Par;
            return {
              id: normalizeGuid(tee.id ?? tee.Id ?? null) ?? `tee-${Date.now()}-${Math.random().toString(16).slice(2)}`,
              name,
              sex: sex === 'female' || sex === 'unisex' ? sex : 'male',
              courseRating: Number.isFinite(courseRating) ? String(courseRating) : '',
              slopeRating: Number.isFinite(slopeRating) ? String(slopeRating) : '',
              teePar: Number.isFinite(par) ? String(par) : '',
            };
          })
          .filter((tee) => tee.name.length > 0);
        if (incomingTees.length > 0) {
          setTees(
            incomingTees.map((tee) => ({
              id: tee.id,
              name: tee.name,
              sex: tee.sex,
              courseRating: tee.courseRating,
              slopeRating: tee.slopeRating,
            }))
          );
          const firstValidPar = incomingTees.find((tee) => tee.teePar.length > 0)?.teePar;
          if (firstValidPar) {
            setTeePar(firstValidPar);
          }
        }
      } finally {
        if (!cancelled) {
          setHydratingCourse(false);
        }
      }
    };

    void loadRoundCourse();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, effectiveRoundId, isNavigationReady, user?.token]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const updateHole = (index: number, patch: Partial<HoleRow>) => {
    setHoles((prev) => prev.map((hole, idx) => (idx === index ? { ...hole, ...patch } : hole)));
  };

  const addTee = () => {
    setTees((prev) => [
      ...prev,
      {
        id: `tee-${Date.now()}-${prev.length + 1}`,
        name: '',
        sex: 'male',
        courseRating: '',
        slopeRating: '',
      },
    ]);
  };

  const removeTee = (id: string) => {
    setTees((prev) => prev.filter((tee) => tee.id !== id));
  };

  const updateTee = (id: string, patch: Partial<TeeRow>) => {
    setTees((prev) => prev.map((tee) => (tee.id === id ? { ...tee, ...patch } : tee)));
  };

  const validateHoles = () => {
    const parsed = holes.map((hole) => ({
      number: hole.number,
      par: Number.parseInt(hole.par, 10),
      strokeIndex: Number.parseInt(hole.strokeIndex, 10),
    }));
    if (parsed.some((hole) => !Number.isFinite(hole.par) || hole.par <= 0)) {
      return { ok: false, message: t('courseConfig.invalidPar') };
    }
    if (parsed.some((hole) => !Number.isFinite(hole.strokeIndex) || hole.strokeIndex <= 0)) {
      return { ok: false, message: t('courseConfig.invalidStrokeIndex') };
    }
    return { ok: true, holes: parsed } as const;
  };

  const validateTees = () => {
    if (tees.length === 0) {
      return { ok: false, message: t('courseConfig.teesRequired') } as const;
    }
    const parsedTeePar = Number.parseInt(teePar, 10);
    if (!Number.isFinite(parsedTeePar) || parsedTeePar <= 0) {
      return { ok: false, message: t('courseConfig.invalidTeePar') } as const;
    }

    const parsed = tees.map((tee) => ({
      name: tee.name.trim(),
      sex: tee.sex.trim().toLowerCase(),
      teeColor: tee.name.trim().toLowerCase(),
      courseRating: Number.parseFloat(tee.courseRating),
      slopeRating: Number.parseInt(tee.slopeRating, 10),
      par: parsedTeePar,
    }));

    if (parsed.some((tee) => !tee.name)) {
      return { ok: false, message: t('courseConfig.teeNameRequired') } as const;
    }
    if (parsed.some((tee) => tee.sex !== 'male' && tee.sex !== 'female' && tee.sex !== 'unisex')) {
      return { ok: false, message: t('courseConfig.teeGenderRequired') } as const;
    }
    if (parsed.some((tee) => !Number.isFinite(tee.courseRating) || tee.courseRating <= 0)) {
      return { ok: false, message: t('courseConfig.invalidCourseRating') } as const;
    }
    if (parsed.some((tee) => !Number.isFinite(tee.slopeRating) || tee.slopeRating <= 0)) {
      return { ok: false, message: t('courseConfig.invalidSlopeRating') } as const;
    }
    return { ok: true, tees: parsed } as const;
  };

  const onSave = () => {
    if (!clubName.trim()) {
      setSaveState({ status: 'error', message: t('courseConfig.clubRequired') });
      return;
    }
    const validation = validateHoles();
    if (!validation.ok) {
      setSaveState({ status: 'error', message: validation.message });
      return;
    }
    const teeValidation = validateTees();
    if (!teeValidation.ok) {
      setSaveState({ status: 'error', message: teeValidation.message });
      return;
    }

    const requestId = createRequestId();
    const payload = {
      type: 'golfCourse.upsert',
      requestId,
      courseId: editingCourseId ?? undefined,
      source: 'user',
      userId: user.id,
      clubName: clubName.trim(),
      courseName: courseName.trim(),
      holes: validation.holes,
      tees: teeValidation.tees,
    };

    const socket = socketRef.current;
    const sent = socket.send(payload);
    pendingRequestIdRef.current = requestId;
    setSaveState({
      status: 'saving',
      message: sent ? t('courseConfig.saving') : t('courseConfig.waitingConnection'),
    });
  };

  const genderOptions = [
    { value: 'male', label: t('courseConfig.genderMale') },
    { value: 'female', label: t('courseConfig.genderFemale') },
    { value: 'unisex', label: t('courseConfig.genderUnisex') },
  ] as const;

  const resolveGenderLabel = (value: string) => {
    return genderOptions.find((option) => option.value === value)?.label ?? t('courseConfig.genderMale');
  };

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.card}>
        <Text style={styles.title}>{t('courseConfig.title')}</Text>
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.clubLabel')}</Text>
            <TextInput
              style={styles.input}
              value={clubName}
              onChangeText={setClubName}
              placeholder={t('courseConfig.clubPlaceholder')}
              placeholderTextColor="rgba(226,232,240,0.6)"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.courseLabel')}</Text>
            <TextInput
              style={styles.input}
              value={courseName}
              onChangeText={setCourseName}
              placeholder={t('courseConfig.coursePlaceholder')}
              placeholderTextColor="rgba(226,232,240,0.6)"
            />
          </View>
        </View>
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.teeParLabel')}</Text>
            <TextInput style={styles.input} value={teePar} onChangeText={setTeePar} keyboardType="number-pad" />
          </View>
        </View>
        <View style={styles.teeHeaderRow}>
          <Text style={styles.teeSectionTitle}>{t('courseConfig.teesTitle')}</Text>
          <Pressable style={styles.addTeeButton} onPress={addTee}>
            <Text style={styles.addTeeButtonText}>{t('courseConfig.addTeeButton')}</Text>
          </Pressable>
        </View>
        {tees.map((tee, index) => (
          <View key={tee.id} style={styles.teeCard}>
            <View style={styles.teeCardTopRow}>
              <TextInput
                style={styles.teeTitleInput}
                value={tee.name}
                onChangeText={(value) => updateTee(tee.id, { name: value })}
                placeholder={t('courseConfig.teeLabel', { index: index + 1 })}
                placeholderTextColor="rgba(226,232,240,0.55)"
              />
              <Pressable
                style={[styles.removeTeeButton, tees.length <= 1 && styles.removeTeeButtonDisabled]}
                onPress={() => removeTee(tee.id)}
                disabled={tees.length <= 1}
              >
                <Text style={styles.removeTeeButtonText}>{t('courseConfig.removeTeeButton')}</Text>
              </Pressable>
            </View>
            <View style={styles.teeInputsRow}>
              <View style={[styles.teeInputGroup, styles.teeInputGroupGender]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.teeGenderLabel')}</Text>
                <Pressable style={styles.genderDropdownButton} onPress={() => setGenderPickerTeeId(tee.id)}>
                  <Text style={[styles.genderDropdownValue, styles.teeFieldValue]}>{resolveGenderLabel(tee.sex)}</Text>
                  <Text style={styles.genderDropdownChevron}>v</Text>
                </Pressable>
              </View>
              <View style={[styles.teeInputGroup, styles.teeInputGroupRating]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.courseRatingLabel')}</Text>
                <TextInput
                  style={[styles.input, styles.teeFieldInput]}
                  value={tee.courseRating}
                  onChangeText={(value) => updateTee(tee.id, { courseRating: value })}
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.teeInputGroup, styles.teeInputGroupSlope]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.slopeRatingLabel')}</Text>
                <TextInput
                  style={[styles.input, styles.teeFieldInput]}
                  value={tee.slopeRating}
                  onChangeText={(value) => updateTee(tee.id, { slopeRating: value })}
                  keyboardType="number-pad"
                />
              </View>
            </View>
          </View>
        ))}
        <View style={styles.tableHeader}>
          <Text style={[styles.tableCell, styles.holeCell]}>{t('courseConfig.holeHeader')}</Text>
          <Text style={[styles.tableCell, styles.parCell]}>{t('courseConfig.parHeader')}</Text>
          <Text style={[styles.tableCell, styles.strokeCell]} numberOfLines={1} ellipsizeMode="tail">
            {t('courseConfig.strokeIndexHeader')}
          </Text>
        </View>
        {holes.map((hole, index) => (
          <View key={hole.number} style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.holeCell]}>{hole.number}</Text>
            <TextInput
              style={[styles.tableCell, styles.parCell, styles.tableInput]}
              value={hole.par}
              onChangeText={(value) => updateHole(index, { par: value })}
              keyboardType="number-pad"
            />
            <TextInput
              style={[styles.tableCell, styles.strokeCell, styles.tableInput]}
              value={hole.strokeIndex}
              onChangeText={(value) => updateHole(index, { strokeIndex: value })}
              keyboardType="number-pad"
            />
          </View>
        ))}
        <View style={styles.actionRow}>
          <Pressable
            style={[styles.cancelButton, saveState.status === 'saving' && styles.actionButtonDisabled]}
            onPress={() => router.back()}
            disabled={saveState.status === 'saving'}
          >
            <Text style={styles.cancelButtonText}>{t('courseConfig.cancelButton')}</Text>
          </Pressable>
          <Pressable
            style={[styles.saveButton, (saveState.status === 'saving' || hydratingCourse || !clubName.trim()) && styles.actionButtonDisabled]}
            onPress={onSave}
            disabled={saveState.status === 'saving' || hydratingCourse || !clubName.trim()}
          >
            {saveState.status === 'saving' || hydratingCourse ? <ActivityIndicator color={appColors.text.primary} /> : null}
            <Text style={styles.saveButtonText}>{t('courseConfig.saveButton')}</Text>
          </Pressable>
        </View>
        {saveState.message ? (
          <Text style={saveState.status === 'error' ? styles.errorText : styles.statusText}>{saveState.message}</Text>
        ) : null}
      </View>
      <Modal visible={genderPickerTeeId !== null} transparent animationType="fade" onRequestClose={() => setGenderPickerTeeId(null)}>
        <Pressable style={styles.pickerOverlay} onPress={() => setGenderPickerTeeId(null)}>
          <View style={styles.pickerCard}>
            {genderOptions.map((option) => (
              <Pressable
                key={option.value}
                style={styles.pickerOption}
                onPress={() => {
                  if (genderPickerTeeId) {
                    updateTee(genderPickerTeeId, { sex: option.value });
                  }
                  setGenderPickerTeeId(null);
                }}
              >
                <Text style={styles.pickerOptionText}>{option.label}</Text>
              </Pressable>
            ))}
          </View>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: '#011806',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  inputRow: {
    gap: 12,
  },
  inputGroup: {
    gap: 6,
  },
  label: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: appColors.text.primary,
  },
  input: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: '#A1D493',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 1,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: 'rgba(51,69,109,0.6)',
  },
  tableCell: {
    fontSize: 14,
    color: '#C8C8B0',
  },
  holeCell: {
    width: 60,
  },
  parCell: {
    width: 80,
  },
  strokeCell: {
    width: 104,
  },
  tableInput: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#07200D',
    color: appColors.text.primary,
  },
  teeHeaderRow: {
    marginTop: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 12,
  },
  teeSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: appColors.text.primary,
  },
  addTeeButton: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    backgroundColor: '#0C2510',
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  addTeeButtonText: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '700',
  },
  teeCard: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    backgroundColor: '#07200D',
    padding: 12,
    rowGap: 10,
  },
  teeCardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 10,
  },
  teeTitleInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#07200D',
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    marginRight: 8,
  },
  removeTeeButton: {
    borderWidth: 1,
    borderColor: '#7f1d1d',
    borderRadius: 10,
    backgroundColor: '#991b1b',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  removeTeeButtonDisabled: {
    opacity: 0.45,
  },
  removeTeeButtonText: {
    color: '#fee2e2',
    fontSize: 12,
    fontWeight: '700',
  },
  teeInputsRow: {
    flexDirection: 'row',
    columnGap: 6,
    flexWrap: 'nowrap',
    alignItems: 'flex-end',
  },
  teeInputGroup: {
    flex: 1,
    gap: 6,
    minWidth: 0,
  },
  teeInputGroupGender: {
    flex: 1.2,
  },
  teeInputGroupRating: {
    flex: 1,
  },
  teeInputGroupSlope: {
    flex: 1,
  },
  genderDropdownButton: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#07200D',
  },
  genderDropdownValue: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  genderDropdownChevron: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  pickerCard: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    overflow: 'hidden',
  },
  pickerOption: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderColor: 'rgba(51,69,109,0.6)',
  },
  pickerOptionText: {
    color: appColors.text.primary,
    fontSize: 15,
  },
  teeFieldLabel: {
    fontSize: 9,
  },
  teeFieldInput: {
    fontSize: 12,
  },
  teeFieldValue: {
    fontSize: 11,
  },
  actionRow: {
    marginTop: 12,
    flexDirection: 'row',
    columnGap: 10,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0C2510',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.secondary,
  },
  saveButton: {
    flex: 1.2,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  saveButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.inverse,
  },
  statusText: {
    color: appColors.text.primary,
  },
  errorText: {
    color: '#b91c1c',
  },
});
